/* eslint-disable no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { type JSX } from 'react';
import { Box, Chip, Divider, IconButton, List, Paper, Stack, Tooltip, Typography } from '@mui/material';
// import Link from '@mui/material/Link';
import { ChannelData, useSortedData } from '../../../hooks/useSortedData';
import CustomIcon from '../../../components/CustomIcon';

import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
// import HelpCenter from '@mui/icons-material/HelpCenter';
// import Refresh from '@mui/icons-material/Refresh';
import { WidgetFooter } from './WidgetFooter';
import { CustomTransComponent, GroupPath } from 'components';
import { useTransformData } from 'hooks/useTransformData';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { WidgetHeader } from './WidgetHeader';
import { InfoListItem } from '@brightlayer-ui/react-components';
import { Refresh } from '@mui/icons-material';
import { LocationText } from 'pages/Properties/LocationText';
import { useRBAC } from 'hooks';
import { useIconLibraryFlag } from '@fiji/common/src/hooks';
import { useTypedSelector } from '@fiji/common';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

export const Component = (props: any): JSX.Element => {
    const mode = props?.mode;
    const widgetData = props?.widgetData;
    const onHeightChange = props?.onHeightChange;
    const { sortedData, ids } = useSortedData(props?.widgetData ?? [], ['primary', 'secondary']);
    const { convertDateTime, pointConversion } = useTransformData();
    const componentRef = React.useRef<HTMLDivElement | null>(null);
    // Separate ref for content wrapper — flex container for layout context
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    // Ref for body Stack — measure scrollHeight (natural content) for grid auto-sizing
    const bodyRef = React.useRef<HTMLDivElement | null>(null);
    const statusContainerRef = React.useRef<HTMLDivElement | null>(null);
    const [heroPrimaryCount, setHeroPrimaryCount] = React.useState<number>(sortedData?.primary?.length);
    const handleResizeComponent = (): void => {
        const containerWidth = componentRef.current?.getBoundingClientRect().width ?? 0;
        if (!containerWidth) return;
        const status = statusContainerRef.current;
        if (!status || !status.children.length) {
            setHeroPrimaryCount(Math.floor(containerWidth / 160));
            return;
        }
        let total = 0;
        let count = 0;
        for (let i = 0; i < status.children.length; i++) {
            const w = (status.children[i] as HTMLElement).getBoundingClientRect().width;
            if (total + w > containerWidth) break;
            total += w;
            count++;
        }
        setHeroPrimaryCount(Math.max(1, count || Math.floor(containerWidth / 160)));
    };

    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const { isIconLibraryEnabled } = useIconLibraryFlag();
    const canViewProperties = hasPermission('device-properties_view-devices');
    const canViewPropertiesExpired = hasPermission('device-properties_view-devices_hasExpired');
    const canViewSettings = hasPermission('device-settings_view-devices');
    const canViewSettingsExpired = hasPermission('device-settings_view-devices_hasExpired');
    const canViewDetails = hasPermission('device-details_view-devices');
    const canViewDetailsExpired = hasPermission('device-details_view-devices_hasExpired');
    const tabsData = useTypedSelector((state: any) => state.common.tabsList);

    const getIsVisable = (): boolean => {
        if (props?.widgetData?.widgetTypeId === 'properties') {
            return canViewProperties || tabsData?.includes('properties') || canViewPropertiesExpired;
        } else if (props?.widgetData?.widgetTypeId === 'settings') {
            return canViewSettings || canViewSettingsExpired || tabsData?.includes('settings');
        }
        return canViewDetails || canViewDetailsExpired;
    };

    const getWidgetLabel = (): any => {
        if (props?.widgetData?.widgetTypeId === 'properties') {
            return 'Property Name';
        } else if (props?.widgetData?.widgetTypeId === 'settings') {
            return 'Settings Name';
        }
        return 'Secondary...';
    };
    React.useEffect(() => {
        handleResizeComponent();
        const observer = new ResizeObserver(handleResizeComponent);
        const el = componentRef.current;
        if (el) observer.observe(el);
        window.addEventListener('resize', handleResizeComponent);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', handleResizeComponent);
        };
    }, []);
    // Re-evaluate when primary data arrives — useLayoutEffect ensures status DOM is measured after DOM update
    React.useLayoutEffect(() => {
        handleResizeComponent();
    }, [sortedData?.primary?.length]);

    // Calculate natural (unscrolled) content height for grid auto-sizing.
    // Breakdown: contentRef contains header + body + footer. To get natural height, measure
    // the fixed chrome (header/footer) and add it to the full body content height (including overflow).
    const calculateNaturalHeight = (): number => {
        const nonBodyChromeHeight = contentRef.current!.offsetHeight - bodyRef.current!.offsetHeight;
        const bodyNaturalHeight = bodyRef.current!.scrollHeight; // Full body content (including overflow rows)
        return nonBodyChromeHeight + bodyNaturalHeight;
    };

    // Report natural content height to parent so the grid cell auto-sizes.
    // Only fires for primary-only widgets (hero icons, no secondary rows) — these should
    // auto-size to their content. Secondary-row widgets stay at their saved grid height
    // and rely on the Stack overflowY:auto scrollbar when content overflows.
    React.useLayoutEffect(() => {
        if (!props?.onHeightChange || !contentRef.current || !bodyRef.current) return;
        // Skip auto-sizing for secondary-row widgets (all modes) — let saved height + scrollbar handle overflow
        if (!isPrimaryOnly()) return;
        props.onHeightChange(calculateNaturalHeight());
    }, [
        sortedData?.primary?.length,
        sortedData?.secondary?.length,
        heroPrimaryCount,
        props?.widgetData?.showWidgetTitleBar,
        props?.widgetData?.showLastUpdateDate,
        props?.widgetData?.viewAllButton,
        props?.widgetData?.isTotalCountVisible,
    ]);
    const safeHeroPrimaryCount = heroPrimaryCount ?? 0;

    const renderPrimaryData = (data: any): JSX.Element => {
        const hasListBelow =
            Boolean(data?.length) &&
            (sortedData?.primary?.length > safeHeroPrimaryCount || Boolean(remainingSecondaryCount()));
        return (
            <Stack
                className={`${hasListBelow ? 'border-bottom-2' : ''} padding-y-8`}
                direction={'row'}
                justifyContent={'space-around'}
                sx={{ overflow: 'hidden', flexShrink: 0 }}
            >
                {data?.map((item: any) => {
                    const iconName = item?.icon?.web?.name ?? (isIconLibraryEnabled ? item?.icon?.name : undefined);
                    const iconFamily =
                        item?.icon?.web?.family ?? (isIconLibraryEnabled ? item?.icon?.iconLibrary?.name : undefined);
                    return (
                        <Stack
                            key={item?.channelId}
                            justifyContent={'center'}
                            alignItems={'center'}
                            sx={{ flex: '0 0 auto', px: '8px' }}
                        >
                            {iconName ? (
                                <CustomIcon
                                    iconName={iconName ?? ''}
                                    family={iconFamily ?? ''}
                                    iconProps={{
                                        sx: { fontSize: '40px', ...(item?.channelId && { color: '#007BC1' }) },
                                    }}
                                />
                            ) : (
                                <Box sx={{ height: '40px' }} />
                            )}
                            <Stack
                                direction={'row'}
                                alignItems={'baseline'}
                                justifyContent={'center'}
                                sx={{ flexWrap: 'nowrap' }}
                            >
                                <Typography variant="body1" fontWeight={'600'} sx={{ whiteSpace: 'nowrap' }}>
                                    {item?.value !== 'true' && item?.value !== 'false'
                                        ? pointConversion(item?.value) || '--'
                                        : item.value}
                                </Typography>
                                {item?.value !== 'true' && item?.value !== 'false' && (
                                    <Typography
                                        variant="body1"
                                        fontWeight={'600'}
                                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                                    >
                                        {item?.unit ? `\u00A0${item.unit}` : ''}
                                    </Typography>
                                )}
                            </Stack>
                            <Typography
                                variant="body2"
                                fontWeight={'400'}
                                fontSize={'14px'}
                                sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}
                            >
                                {item?.displayName ?? 'Channel'}
                            </Typography>
                            {props?.widgetData?.multiDevice && (
                                <Typography
                                    variant="body2"
                                    fontWeight={'300'}
                                    fontSize={'14px'}
                                    sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}
                                >
                                    {item?.deviceName ?? ''}
                                </Typography>
                            )}
                        </Stack>
                    );
                })}
            </Stack>
        );
    };

    const getTagValue = (value: any[]): JSX.Element => (
        <Stack direction={'row'} className="tag-truncate" spacing={1} alignItems={'center'}>
            {value?.length ? value?.map((item: any) => <Chip key={item?.id} label={item?.name} />) : '-'}
        </Stack>
    );

    const getRightComponent = (item: any): JSX.Element => {
        if (Array.isArray(item?.value)) {
            return getTagValue(item?.value);
        }
        if (item?.channelName === 'batteryinstalldate' || item?.channelName === 'deviceinstalldate') {
            return (
                <>
                    {item?.value
                        ? convertDateTime({
                              timestamp: item?.value,
                              customFormat: 'LL',
                          })
                        : '--'}
                </>
            );
        }
        if (item?.channelName === 'location') {
            return (
                <LocationText
                    {...(componentRef.current?.getBoundingClientRect().width && {
                        maxWidth: `${Math.floor((componentRef.current?.getBoundingClientRect().width / 100) * 70)}px`,
                    })}
                    data={{
                        ...item,
                        key: 'location',
                        value: {
                            lat: item?.value?.lat,
                            lng: item?.value?.lon,
                        },
                    }}
                />
            );
        }
        if (item?.channelName === 'description') {
            return (
                <Typography
                    sx={{
                        wordBreak: 'break-word',
                        maxWidth: componentRef.current?.getBoundingClientRect().width
                            ? `${Math.floor((componentRef.current?.getBoundingClientRect().width / 100) * 70)}px`
                            : '650px',
                    }}
                >
                    {item?.value ? item?.value : '-'}
                </Typography>
            );
        }

        const isBooleanValue = item?.value === 'true' || item?.value === 'false';
        const convertedValue = pointConversion(item?.value) || '--';
        const valueWithOptionalUnit = item?.unit ? `${convertedValue} ${item?.unit}` : `${convertedValue}`;

        return (
            <Typography
                variant="body1"
                fontWeight={'600'}
                noWrap
                title={isBooleanValue ? item?.value : valueWithOptionalUnit}
                sx={{
                    display: 'block',
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {isBooleanValue ? item.value : valueWithOptionalUnit}
            </Typography>
        );
    };

    const renderSecondaryData = (data: any, sectionStart = false): JSX.Element => {
        const isProperties = widgetData?.widgetTypeId === 'properties';
        const isMultiDevice = widgetData?.multiDevice;
        const widgetTypeId = widgetData?.widgetTypeId;

        if (data?.length) {
            return data.map((item: ChannelData, index: number) => {
                const displayName = item?.displayName ?? getWidgetLabel();
                const iconName = item?.icon?.web?.name ?? (isIconLibraryEnabled ? item?.icon?.name : undefined);
                const iconFamily =
                    item?.icon?.web?.family ?? (isIconLibraryEnabled ? item?.icon?.iconLibrary?.name : undefined);
                const devicePath = item?.devicePath ?? [];
                const hasBorderBottom = index < data.length - 1;
                const hasSectionStartSeparator = sectionStart && index === 0;

                return (
                    <InfoListItem
                        hidePadding={!hasAnySecondaryIcon}
                        key={item?.channelId}
                        sx={{
                            ...(isProperties
                                ? { height: 'auto', minHeight: '4.5rem' }
                                : { minHeight: shouldUseCompactSecondaryRow ? '2rem' : '3rem' }),
                            ...(hasBorderBottom && {
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                            }),
                            ...(hasSectionStartSeparator && {
                                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                borderColor: 'divider',
                            }),
                            '&.MuiListItem-root': {
                                paddingTop: '2px',
                                paddingBottom: '2px',
                            },
                            '.BluiInfoListItem-info': {
                                minWidth: 0,
                                width: '100%',
                                overflow: 'hidden',
                            },
                            '.BluiInfoListItem-leftComponent': {
                                flex: '1 1 52%',
                                minWidth: 0,
                                maxWidth: '52%',
                                overflow: 'hidden',
                            },
                            '.BluiInfoListItem-title': {
                                minWidth: 0,
                                width: '100%',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                            '.BluiInfoListItem-rightComponent': {
                                flex: '0 1 48%',
                                minWidth: 0,
                                width: '100%',
                                maxWidth: '48%',
                                overflow: 'hidden',
                            },
                            '.BluiInfoListItem-rightComponent .MuiTypography-root': {
                                minWidth: 0,
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                textAlign: 'right',
                            },
                        }}
                        className="list-item-hover"
                        title=""
                        leftComponent={
                            <Box sx={{ minWidth: 0, width: '100%', maxWidth: '100%' }}>
                                <Typography
                                    variant="body1"
                                    fontWeight={'600'}
                                    noWrap
                                    title={displayName}
                                    sx={{
                                        display: 'block',
                                        minWidth: 0,
                                        width: '100%',
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {displayName}
                                </Typography>
                                {isMultiDevice && (
                                    <GroupPath pathName={devicePath} deviceName={item?.deviceName} width="250px" />
                                )}
                            </Box>
                        }
                        {...(hasAnySecondaryIcon && {
                            icon: <CustomIcon iconName={iconName ?? ''} family={iconFamily ?? ''} />,
                        })}
                        rightComponent={getRightComponent(item)}
                    />
                );
            });
        }
        return !sortedData?.primary ? (
            <CustomEmptyState icon={<FormatListBulleted fontSize={'inherit'} />} title={`No ${widgetTypeId} Found`} />
        ) : (
            <></>
        );
    };

    const extraPrimaryCount = (): number => {
        const hero = heroPrimaryCount ?? 0;
        const primary = sortedData?.primary?.length ?? 0;
        return primary > hero ? primary - hero : 0;
    };

    const remainingSecondaryCount = (): number => {
        const secondaryLength = sortedData?.secondary?.length ?? 0;
        const rawSecondaryLimit = Number(props?.widgetData?.secondaryChannelCount);
        const normalizedSecondaryLimit = Number.isFinite(rawSecondaryLimit) ? rawSecondaryLimit : secondaryLength;
        const effectiveSecondaryLimit = Math.max(normalizedSecondaryLimit, 0);

        if (sortedData?.primary?.length) {
            // Render max allowed secondaries independently from any overflowed primary rows.
            return Math.min(secondaryLength, effectiveSecondaryLimit);
        }

        // Secondary-only: in edit mode keep placeholders, in view mode show only real data.
        return props?.mode?.includes('view')
            ? Math.min(secondaryLength, effectiveSecondaryLimit)
            : Math.max(secondaryLength, effectiveSecondaryLimit);
    };

    const getLastUpdatedTime = (): any => {
        const timestamp =
            typeof props?.widgetData?.updatedAt === 'string'
                ? new Date(props?.widgetData?.updatedAt).getTime()
                : props?.widgetData?.updatedAt ?? Date.now();

        return `${convertDateTime({
            timestamp,
            customFormat: 'MM/DD/YYYY',
        })} ${convertDateTime({
            timestamp,
            customFormat: 'HH:mm',
        })}`;
    };

    const getDynamicHeight = (): string =>
        `calc(100% - ${props?.mode === 'edit' || props?.widgetData?.viewAllButton ? '97px' : '49px'})`;

    const primaryOverflows = (): boolean => sortedData?.primary?.length > safeHeroPrimaryCount;

    // True if at least one secondary (or overflow primary) item has an icon — controls left padding alignment
    const hasAnySecondaryIcon: boolean = [...(sortedData?.secondary ?? []), ...(sortedData?.primary ?? [])].some(
        (item: any) => item?.icon?.web?.name || (isIconLibraryEnabled ? item?.icon?.name : undefined)
    );

    // Card has ONLY primary (hero icons), no secondary rows and no overflow into a list
    const isPrimaryOnly = (): boolean =>
        Boolean(sortedData?.primary?.length) && !sortedData?.secondary?.length && !primaryOverflows();

    const isViewMode = Boolean(mode?.includes('view'));
    const isPinnedViewMode = Boolean(mode?.includes('pinned-view'));
    // In view mode the card shrink-wraps to its real content (fit-content) and is capped at the grid
    // cell via maxHeight:100%. This prevents the ceiling-rounding remainder (content height snapped up to
    // whole grid rows) from being rendered as white in-card whitespace below the last channel row — the
    // leftover space stays as the dashboard's grey inter-widget gap instead.
    const shouldShrinkWrapViewContent =
        isViewMode && (isPinnedViewMode || Boolean(onHeightChange) || isPrimaryOnly() || !sortedData?.primary?.length);
    const footerReservedSpace = isPinnedViewMode ? '40px' : '48px';
    const shouldRenderFooter =
        getIsVisable() &&
        (mode === 'edit' || widgetData?.viewAllButton || widgetData?.displayViewBar || widgetData?.isViewAllDisplayed);
    const shouldUseCompactSecondaryRow = isViewMode && !shouldRenderFooter;
    // The WidgetFooter is rendered in-flow (position: sticky) and already occupies its own height,
    // so no extra bottom padding is needed when the card shrink-wraps to content. Reserving it there
    // double-counts the footer height and shows as white space between the last row and the footer.
    // Only reserve footer space in the (non shrink-wrap) fill case where the body can scroll under it.
    const shouldReserveFooterSpaceInView = shouldRenderFooter && isViewMode && !shouldShrinkWrapViewContent;
    const shouldFillEditHeight = mode === 'edit' || mode === 'pinned-edit';
    const showWidgetTitleBar = widgetData?.showWidgetTitleBar ?? true;
    const isTotalCountVisible = props?.widgetData?.isTotalCountVisible !== false;
    const totalCount = (sortedData?.primary?.length ?? 0) + (sortedData?.secondary?.length ?? 0);
    const showLastUpdateDate =
        'primary' in (props?.widgetData ?? {}) && props?.widgetData?.showLastUpdateDate !== false;
    const hasOverflowPrimaryRows = sortedData?.primary?.length > safeHeroPrimaryCount;
    const visibleSecondaryCount = remainingSecondaryCount();
    const hasVisibleSecondaryRows = Boolean(visibleSecondaryCount);
    const showHeaderDivider = mode !== 'edit' && showWidgetTitleBar;
    const paperHeight = shouldFillEditHeight ? '100%' : !isViewMode ? 'auto' : '100%'; // Always fill grid cell in view mode to enable scrolling

    return (
        <Paper
            style={{ height: paperHeight }}
            sx={{
                overflow: 'hidden',
                position: 'relative',
                ...(isViewMode && {
                    display: 'flex',
                    flexDirection: 'column',
                }),
            }}
            ref={componentRef}
        >
            {/* Hidden status row: renders all primary items at natural width to measure how many fit in heroPrimaryCount */}
            <Box
                ref={statusContainerRef}
                sx={{
                    position: 'absolute',
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'row',
                    top: 0,
                    left: 0,
                    zIndex: -1,
                }}
            >
                {sortedData?.primary?.map((item: any) => (
                    <Box key={item?.channelId} sx={{ flex: '0 0 auto', px: '8px' }}>
                        <Typography variant="body1" fontWeight={600} sx={{ whiteSpace: 'nowrap', display: 'block' }}>
                            {item?.value !== 'true' && item?.value !== 'false'
                                ? `${pointConversion(item?.value) || '--'}${item?.unit ? `\u00A0${item.unit}` : ''}`
                                : item?.value}
                        </Typography>
                        <Typography variant="body2" fontSize="14px" sx={{ whiteSpace: 'nowrap', display: 'block' }}>
                            {item?.displayName ?? 'Channel'}
                        </Typography>
                    </Box>
                ))}
            </Box>
            {/* Inner content wrapper — flex layout container (preserves flex context for inner Stack);
                no height constraint so contentRef measures natural content height for grid auto-sizing */}
            <div
                ref={contentRef}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                {showWidgetTitleBar && (
                    <WidgetHeader
                        mode={props?.mode}
                        widgetData={props?.widgetData}
                        count={totalCount}
                        headerAction={
                            !props?.widgetData?.multiDevice &&
                            Object.keys(props?.widgetData ?? {}).includes('primary') &&
                            props?.widgetData?.showLastUpdateDate && (
                                <Stack
                                    direction={'row'}
                                    spacing={1}
                                    alignItems={'center'}
                                    flexShrink={1}
                                    sx={{ minWidth: 0, ml: 1 }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight={'300'}
                                        noWrap
                                        sx={{
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            display: 'block',
                                        }}
                                    >
                                        <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:LAST_UPDATED'} />{' '}
                                        <Tooltip title={getLastUpdatedTime()} placement="bottom" arrow>
                                            <b className="fw-600">{getLastUpdatedTime()}</b>
                                        </Tooltip>
                                    </Typography>
                                    <IconButton
                                        sx={{ margin: '0px !important', flexShrink: 0 }}
                                        disabled={!props?.mode?.includes('view')}
                                        onClick={(): void => props?.refreshCallback?.()}
                                    >
                                        <Refresh />
                                    </IconButton>
                                </Stack>
                            )
                        }
                    />
                )}
                {showHeaderDivider && <Divider />}
                <Stack
                    ref={bodyRef}
                    sx={{
                        overflowX: 'hidden',
                        ...(mode === 'edit' && {
                            pt: showWidgetTitleBar ? '48px' : 0,
                            pb: '48px',
                        }),
                        // Keep secondary-card heights uniform regardless of View All footer visibility.
                        // Footer is absolute, so reserve equal bottom space in view mode for all secondary cards.
                        ...(shouldReserveFooterSpaceInView && { pb: footerReservedSpace }),
                        // Only enable flex scrolling for widgets with secondary rows AND a footer to align to.
                        // Without footer, let content shrink-wrap naturally to avoid white space below.
                        ...(isViewMode &&
                            hasVisibleSecondaryRows &&
                            shouldRenderFooter && {
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                            }),
                    }}
                >
                    {
                        sortedData?.primary &&
                            (sortedData?.primary?.length > 0
                                ? renderPrimaryData(sortedData?.primary.slice(0, safeHeroPrimaryCount))
                                : sortedData?.secondary?.length === 0
                                ? renderPrimaryData(Array.from({ length: !props?.mode?.includes('view') ? 3 : 0 }))
                                : null) /* secondary-only: skip empty primary render to avoid extra padding */
                    }
                    {hasOverflowPrimaryRows && (
                        <List disablePadding sx={{ flexShrink: 0 }}>
                            {renderSecondaryData(
                                sortedData?.primary.slice(
                                    safeHeroPrimaryCount,
                                    safeHeroPrimaryCount + extraPrimaryCount()
                                )
                            )}
                        </List>
                    )}
                    {hasVisibleSecondaryRows && (
                        <List disablePadding sx={{ flexShrink: 0 }}>
                            {sortedData?.secondary?.length > 0 || sortedData?.primary?.length > 0
                                ? renderSecondaryData(
                                      sortedData?.secondary?.slice(0, visibleSecondaryCount),
                                      hasOverflowPrimaryRows
                                  )
                                : renderSecondaryData(
                                      Array.from({
                                          length: !props?.mode?.includes('view')
                                              ? Math.max(props?.widgetData?.secondaryChannelCount ?? 5)
                                              : 0,
                                      })
                                  )}
                        </List>
                    )}
                </Stack>
                {shouldRenderFooter && <WidgetFooter mode={props?.mode} ids={ids} widgetData={props?.widgetData} />}
            </div>
            {/* end contentRef wrapper */}
        </Paper>
    );
};
